import React, { useState, useRef } from 'react';
import { X, Copy, CheckCircle, Code, Plus, Edit2, Trash2, Check } from 'lucide-react';
import Toast from './Toast';

const EmbedDynamicVariables = ({ agent, onClose }) => {
  const [variables, setVariables] = useState([{ id: Date.now(), name: 'First_Name', label: 'First Name' }]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');
  const [copySuccess, setCopySuccess] = useState({ url: false, code: false });
  const [toast, setToast] = useState(null);
  const newVariableInputRef = useRef(null);
  const editInputRef = useRef(null);
  
  const baseUrl = window.location.origin;
  
  // Convert display name to variable name format
  const sanitizeVariableName = (name) => {
    return name
      .replace(/[^a-zA-Z0-9_\s]/g, '')
      .replace(/\s+/g, '_');
  };
  
  // Add new variable
  const handleAddVariable = () => {
    const varName = newVariableName.trim();
    if (!varName) return;
    
    const sanitizedName = sanitizeVariableName(varName);
    if (!sanitizedName) return;
    
    // Check for duplicates
    if (variables.some(v => v.name === sanitizedName)) {
      setToast({ message: 'Variable already exists', type: 'error' });
      return;
    }
    
    setVariables(prev => [...prev, {
      id: Date.now(),
      name: sanitizedName,
      label: varName
    }]);
    setNewVariableName('');
    setShowAddModal(false);
  };
  
  // Start editing variable
  const startEdit = (variable) => {
    setEditingId(variable.id);
    setEditName(variable.name);
    setEditLabel(variable.label);
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
      }
    }, 100);
  };
  
  // Save edited variable
  const saveEdit = () => {
    if (!editLabel.trim()) return;
    
    const sanitizedName = sanitizeVariableName(editLabel);
    if (!sanitizedName) return;
    
    // Check for duplicates (excluding current variable)
    if (variables.some(v => v.name === sanitizedName && v.id !== editingId)) {
      setToast({ message: 'Variable name already exists', type: 'error' });
      return;
    }
    
    setVariables(prev => prev.map(v => 
      v.id === editingId 
        ? { ...v, name: sanitizedName, label: editLabel.trim() }
        : v
    ));
    setEditingId(null);
    setEditName('');
    setEditLabel('');
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditLabel('');
  };
  
  // Remove variable
  const removeVariable = (id) => {
    if (variables.length <= 1) {
      setToast({ message: 'At least one variable is required', type: 'error' });
      return;
    }
    setVariables(prev => prev.filter(v => v.id !== id));
  };
  
  // Generate embed URL with variable names (empty values - users will fill in on embed page)
  const generateEmbedUrl = () => {
    let url = `${baseUrl}/embed/agent/${agent.id}?view=dynamic`;
    variables.forEach(v => {
      if (v.name.trim()) {
        // Use empty value so embed page shows input field
        url += `&${encodeURIComponent(v.name)}=`;
      }
    });
    return url;
  };
  
  const embedCode = `<iframe src="${generateEmbedUrl()}" width="100%" height="600" frameborder="0" allow="microphone" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`;
  
  const handleCopy = async (text, type = 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(prev => ({ ...prev, [type]: true }));
      setToast({ message: type === 'url' ? 'URL copied to clipboard!' : 'Embed code copied to clipboard!', type: 'success' });
      setTimeout(() => {
        setCopySuccess(prev => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      setToast({ message: 'Failed to copy to clipboard', type: 'error' });
    }
  };
  
  const handleModalKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddVariable();
    } else if (e.key === 'Escape') {
      setShowAddModal(false);
      setNewVariableName('');
    }
  };
  
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        {/* Header */}
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Code className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Embed with Dynamic Variables: {agent.name}</h2>
              <p className="text-white/70 text-xs mt-0.5 font-normal">Create custom variables for personalized conversations</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Variable Management Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Variables</h3>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(true);
                  setTimeout(() => {
                    if (newVariableInputRef.current) {
                      newVariableInputRef.current.focus();
                    }
                  }, 100);
                }}
                className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-xs font-medium"
              >
                <Plus className="w-3 h-3" />
                Add Variable
              </button>
            </div>
            
            <div className="space-y-2">
              {variables.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4 border border-border rounded-lg bg-muted/30">
                  No variables defined. Click "Add Variable" to create one.
                </p>
              ) : (
                variables.map((variable) => (
                  <div key={variable.id} className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg">
                    {editingId === variable.id ? (
                      <>
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editLabel}
                          onChange={(e) => {
                            setEditLabel(e.target.value);
                            const sanitized = sanitizeVariableName(e.target.value);
                            setEditName(sanitized);
                          }}
                          onKeyDown={handleEditKeyDown}
                          className="flex-1 px-3 py-1.5 bg-background border border-border rounded text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Variable label (e.g., First Name)"
                        />
                        <div className="flex items-center gap-1">
                          <button
                            onClick={saveEdit}
                            disabled={!editLabel.trim()}
                            className="p-1.5 text-success hover:bg-success/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-muted-foreground hover:bg-muted rounded transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">{variable.label}</div>
                          <div className="text-xs text-muted-foreground font-mono mt-0.5">
                            Variable: <span className="text-primary">{variable.name}</span> | Use in prompts: <span className="text-primary">{`{{${variable.name}}}`}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(variable)}
                            className="p-1.5 text-muted-foreground hover:bg-muted rounded transition-colors"
                            title="Edit variable"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeVariable(variable.id)}
                            disabled={variables.length <= 1}
                            className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove variable"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Define variables that will be available in your embed. Clients will see input fields for these variables and fill them in before starting the call.
            </p>
          </div>

          {/* Embed URL */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Embed URL</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground break-all">
                {generateEmbedUrl()}
              </div>
              <button
                onClick={() => handleCopy(generateEmbedUrl(), 'url')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.url ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Direct URL to the embed page. Users will see input fields for each variable you defined above.
            </p>
          </div>

          {/* Embed Code (iframe) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Embed Code (iframe)</h3>
            </div>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={embedCode}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground resize-none"
                rows={4}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => handleCopy(embedCode, 'code')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.code ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Copy this iframe code to embed on your website. The embed page will show input fields for each variable you defined. Users fill in values before starting the call.
              Variables are automatically passed to the agent when the call starts.
            </p>
          </div>
        </div>
        
        {/* Add Variable Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl shadow-elegant max-w-md w-full p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Add Variable</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewVariableName('');
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground block">
                  Variable Label
                </label>
                <input
                  ref={newVariableInputRef}
                  type="text"
                  value={newVariableName}
                  onChange={(e) => setNewVariableName(e.target.value)}
                  onKeyDown={handleModalKeyDown}
                  placeholder="e.g., First Name, Email, Phone Number"
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                />
                {newVariableName.trim() && (
                  <p className="text-xs text-muted-foreground">
                    Variable name: <span className="font-mono text-primary">{sanitizeVariableName(newVariableName)}</span> | Use in prompts: <span className="font-mono text-primary">{`{{${sanitizeVariableName(newVariableName)}}}`}</span>
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  This will be used as the field label. Spaces will be converted to underscores.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewVariableName('');
                  }}
                  className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors text-sm font-medium"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddVariable}
                  disabled={!newVariableName.trim() || variables.some(v => v.name === sanitizeVariableName(newVariableName.trim()))}
                  className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  <Check className="w-4 h-4" />
                  Add Variable
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EmbedDynamicVariables;

